import { GoogleGenerativeAI } from "@google/generative-ai";
import { model } from '../firebase/config';
import OpenAI from 'openai';

// Flag to switch between APIs
const USE_OPENAI = true;

// Initialize APIs
const geminiAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
const openAI = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

// Define the search spaces function schema
const searchSpacesSchema = {
    name: "searchSpaces",
    description: "Search for office spaces based on specified criteria",
    parameters: {
        type: "object",
        properties: {
            city: {
                type: "string",
                description: "City"
            },
            micromarket: {
                type: "string",
                description: "Micromarket"
            },
            seats: {
                type: "integer",
                description: "Seats"
            },
            budget: {
                type: "integer",
                description: "Maximum budget in INR"
            }
        },
        required: ["city", "micromarket", "seats"]
    }
};

const extractJSONFromText = (text) => {
  try {
    // Try to parse the entire text as JSON first
    return JSON.parse(text);
  } catch (e) {
    // If that fails, try to find JSON within the text
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        return JSON.parse(jsonMatch[0]);
      } catch (e2) {
        console.error('Error parsing extracted JSON:', e2);
        throw new Error('Invalid JSON format in response');
      }
    }
    throw new Error('No valid JSON found in response');
  }
};

const generateWithOpenAI = async (prompt) => {
  const response = await openAI.chat.completions.create({
    model: "gpt-3.5-turbo-1106",
    messages: [
      {
        role: "system",
        content: "You are a professional data analyst that returns information in markdown format."
      },
      {
        role: "user",
        content: `${prompt}\n\nReturn the response in markdown format.`
      }
    ],
    temperature: 0.1
  });

  return response.choices[0].message.content;
};

const generateWithGemini = async (prompt) => {
  const model = geminiAI.getGenerativeModel({ model: "gemini-1.5-flash" });
  const result = await model.generateContent(prompt + "\n\nProvide ONLY the Markdown formatted content.");
  const response = await result.response;
  const text = response.text();
  return text;
};

export const chatWithGemini = async (userInput, currentParams = null) => {
    try {
        const functionSchema = {
            name: "search",
            description: "Search for office spaces based on parameters",
            parameters: {
                type: "object",
                properties: {
                    city: {
                        type: "string",
                        description: "City name"
                    },
                    micromarket: {
                        type: "string",
                        description: "Area or locality within the city"
                    },
                    seats: {
                        type: "integer",
                        description: "Number of seats required"
                    },
                    budgetFrom: {
                        type: "integer",
                        description: "Minimum budget in INR"
                    },
                    budgetTo: {
                        type: "integer",
                        description: "Maximum budget in INR"
                    },
                    budget: {
                        type: "integer",
                        description: "Exact budget in INR when no range is specified"
                    }
                },
                required: ["city", "micromarket", "seats"]
            }
        };

        let prompt = "";
        if (currentParams) {
            // If we have current parameters, include them in the prompt
            const budgetDisplay = currentParams.budgetFrom && currentParams.budgetTo ? 
                `₹${currentParams.budgetFrom} - ₹${currentParams.budgetTo}` :
                currentParams.budget ? `₹${currentParams.budget}` : 'Not specified';

            prompt = `Current search parameters:
- City: ${currentParams.city}
- Micromarket: ${currentParams.micromarket}
- Seats: ${currentParams.seats}
- Budget: ${budgetDisplay}

User message: "${userInput}"

If the user is specifying a new micromarket, extract it and keep all other parameters the same.
If the user mentions a budget range (e.g., "between 10000 and 15000" or "10000-15000"), extract budgetFrom and budgetTo.
If the user mentions a single budget number, use that as the exact budget.
If the user is modifying other parameters, update them accordingly.
Return all parameters including unchanged ones.

Example responses:
- For "budget between 10000 and 15000": set budgetFrom=10000, budgetTo=15000
- For "budget 12000": set budget=12000
- For "Show me spaces in T Nagar": update micromarket to "T Nagar" and keep other parameters`;
        } else {
            // Initial search prompt
            prompt = `Extract office space search parameters from: "${userInput}"

Required parameters:
- city: The city name
- micromarket: The area or locality within the city
- seats: Number of seats needed

Budget can be specified in two ways:
1. As a range: Extract budgetFrom and budgetTo (e.g., "budget between 10000-15000")
2. As a single value: Extract budget (e.g., "budget 12000")

Return all available parameters.`;
        }

        // Create chat with the model
        const chat = model.startChat({
            tools: [{
                functionDeclarations: [functionSchema]
            }]
        });

        // Send message and get response
        const result = await chat.sendMessage(prompt);
        const response = await result.response;

        // Check if the response includes a function call
        if (response.candidates?.[0]?.content?.parts?.[0]?.functionCall) {
            const functionCall = response.candidates[0].content.parts[0].functionCall;
            const args = functionCall.args;

            // If we have current parameters and the user only specified a micromarket
            if (currentParams && !args.city && args.micromarket) {
                return {
                    searchParams: {
                        ...currentParams,
                        micromarket: args.micromarket
                    }
                };
            }

            return {
                searchParams: args
            };
        }

        // If we have current parameters and the input looks like just a micromarket name
        if (currentParams && userInput.split(' ').length <= 3) {
            // Check if the input might be just a micromarket name
            const possibleMicromarket = userInput.trim();
            return {
                searchParams: {
                    ...currentParams,
                    micromarket: possibleMicromarket
                }
            };
        }

        return { searchParams: null };
    } catch (error) {
        console.error('Error in chatWithGemini:', error);
        throw error;
    }
};

export const generateProfile = async (prompt) => {
  try {
    if (USE_OPENAI) {
      try {
        return await generateWithOpenAI(prompt);
      } catch (openAIError) {
        console.warn('OpenAI error, falling back to Gemini:', openAIError);
        return await generateWithGemini(prompt);
      }
    } else {
      return await generateWithGemini(prompt);
    }
  } catch (error) {
    console.error('Error generating profile:', error);
    throw new Error(`Failed to generate profile: ${error.message}`);
  }
};
